import capitalize from 'lodash/capitalize';
import round from 'lodash/round';

import {
  Divider,
  ExternalCell,
  ExternalRow,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import {
  WorkloadOptimizationMetricSpec,
  WorkloadOptimizationState,
} from '@cast/types';

import { getRecommendationPercentile } from 'features/cluster/workload-optimization/_utils';
import { ScalingPolicyUpdatedEvent } from 'features/cluster/workload-optimization/types';

import { DetailWitTitle } from './DetailsRowContainer';
import { OptimizationStateItem } from './OptimizationStateItem';
import { PodCountItem } from './PodCountItem';
import { ScalingPoliciesSubsection } from './ScalingPoliciesSubsection';
import { ScalingPolicyEventDetailsRowContainer } from './ScalingPolicyEventDetailsRowContainer';

const toPercentage = (val: number) => {
  return `${round(val * 100, 2)}%`;
};
const toSeconds = (val: number | null | undefined) =>
  val != null ? `${val}s` : undefined;
const toOwnership = (
  settings: ScalingPolicyUpdatedEvent['details']['previous']['hpaSettings']
) => (settings != null ? (settings.takeOwnership ? 'Yes' : 'No') : undefined);

type TriggerData = { type: string; value: string };

const formatTriggerValue = (
  value: string | undefined,
  type: string | undefined
) => {
  if (value == null) return '-';
  return type === 'UTILIZATION' ? `${value}%` : value;
};

const getTriggerInfo = (
  metrics: WorkloadOptimizationMetricSpec[]
): Record<string, TriggerData> => {
  const map: Record<string, TriggerData> = {};
  metrics?.forEach((m) => {
    if (m.type === 'RESOURCE' && m.resource) {
      map[m.resource.name] = {
        type: m.resource.target.type,
        value: m.resource.target.value,
      };
    }
  });
  return map;
};

export const ScalingPolicyUpdatedEventSubRow = ({
  event,
}: {
  event: ScalingPolicyUpdatedEvent;
}) => {
  const hasHpaSection =
    !!event.details.previous.hpaSettings?.nativeHpaSpec ||
    !!event.details.current.hpaSettings?.nativeHpaSpec;

  const prevTriggers = getTriggerInfo(
    event.details.previous.hpaSettings?.nativeHpaSpec?.metrics ?? []
  );
  const currTriggers = getTriggerInfo(
    event.details.current.hpaSettings?.nativeHpaSpec?.metrics ?? []
  );
  const triggerNames = Array.from(
    new Set([
      'cpu',
      'memory',
      ...Object.keys(prevTriggers),
      ...Object.keys(currTriggers),
    ])
  );

  const prevBehavior =
    event.details.previous.hpaSettings?.nativeHpaSpec?.behavior;
  const currBehavior =
    event.details.current.hpaSettings?.nativeHpaSpec?.behavior;
  const hasBehaviorSection = !!(prevBehavior || currBehavior);

  return (
    <ScalingPolicyEventDetailsRowContainer event={event}>
      <ExternalRow
        height={212}
        sx={{
          alignItems: 'flex-start',
          borderTop: tokens.theme.border.structural.neutral,
          py: 12,
        }}
      >
        <ExternalCell startColumn={0}>
          <DetailWitTitle title="VERTICAL rightsizing:" titleVariant="medium" />
        </ExternalCell>
        <ExternalCell startColumn={1} endColumn={2}>
          <Stack gap={16}>
            <OptimizationStateItem
              prev={
                event.details.previous.recommendationPolicies
                  .managementOption === WorkloadOptimizationState.MANAGED
              }
              curr={
                event.details.current.recommendationPolicies
                  .managementOption === WorkloadOptimizationState.MANAGED
              }
              label="vertical SCALING POLICY:"
              policyName="overhead"
            />
            <Divider sx={{ width: '100%' }} />
            <Box
              display="grid"
              gridTemplateColumns="repeat(2, 1fr)"
              width="100%"
              columnGap={100}
              rowGap={32}
            >
              {[
                {
                  label: 'overhead:',
                  content: (
                    <>
                      <PodCountItem
                        prev={toPercentage(
                          event.details.previous.recommendationPolicies.cpu
                            .overhead
                        )}
                        curr={toPercentage(
                          event.details.current.recommendationPolicies.cpu
                            .overhead
                        )}
                        label="CPU"
                      />
                      <PodCountItem
                        prev={toPercentage(
                          event.details.previous.recommendationPolicies.memory
                            .overhead
                        )}
                        curr={toPercentage(
                          event.details.current.recommendationPolicies.memory
                            .overhead
                        )}
                        label="MEMORY"
                      />
                    </>
                  ),
                },
                {
                  label: 'recommendation percentile:',
                  content: (
                    <>
                      <PodCountItem
                        prev={
                          getRecommendationPercentile(
                            event.details.previous.recommendationPolicies.cpu
                          ) + '%'
                        }
                        curr={
                          getRecommendationPercentile(
                            event.details.current.recommendationPolicies.cpu
                          ) + '%'
                        }
                        label="CPU"
                      />

                      <PodCountItem
                        prev={
                          getRecommendationPercentile(
                            event.details.previous.recommendationPolicies.memory
                          ) + '%'
                        }
                        curr={
                          getRecommendationPercentile(
                            event.details.current.recommendationPolicies.memory
                          ) + '%'
                        }
                        label="MEMORY"
                      />
                    </>
                  ),
                },
                {
                  label: 'autoscaler mode:',
                  content: (
                    <PodCountItem
                      prev={capitalize(event.details.previous.applyType)}
                      curr={capitalize(event.details.current.applyType)}
                    />
                  ),
                },
                {
                  label: 'change sensitivity:',
                  content: (
                    <>
                      <PodCountItem
                        prev={toPercentage(
                          event.details.previous.recommendationPolicies.cpu
                            .applyThreshold
                        )}
                        curr={toPercentage(
                          event.details.current.recommendationPolicies.cpu
                            .applyThreshold
                        )}
                        label="CPU"
                      />
                      <PodCountItem
                        prev={toPercentage(
                          event.details.previous.recommendationPolicies.memory
                            .applyThreshold
                        )}
                        curr={toPercentage(
                          event.details.current.recommendationPolicies.memory
                            .applyThreshold
                        )}
                        label="MEMORY"
                      />
                    </>
                  ),
                },
              ].map(({ content, label }, index) => (
                <Stack key={index} gap={8}>
                  <DetailWitTitle title={label} titleVariant="strong" />
                  <Stack direction="row" alignItems="center" gap={40}>
                    {content}
                  </Stack>
                </Stack>
              ))}
            </Box>
          </Stack>
        </ExternalCell>
      </ExternalRow>
      {hasHpaSection && (
        <ExternalRow
          minHeight={140}
          sx={{
            alignItems: 'flex-start',
            borderTop: tokens.theme.border.structural.neutral,
            py: 12,
          }}
        >
          <ExternalCell startColumn={0}>
            <DetailWitTitle
              title="HORIZONTAL rightsizing:"
              titleVariant="medium"
            />
          </ExternalCell>
          <ExternalCell startColumn={1} endColumn={2}>
            <Stack gap={16}>
              <OptimizationStateItem
                prev={
                  event.details.previous.hpaSettings?.managementOption ===
                  WorkloadOptimizationState.MANAGED
                }
                curr={
                  event.details.current.hpaSettings?.managementOption ===
                  WorkloadOptimizationState.MANAGED
                }
                label="horizontal SCALING POLICY:"
              />
              <Divider sx={{ width: '100%' }} />
              <Box
                display="grid"
                gridTemplateColumns="repeat(3, 1fr)"
                width="100%"
                columnGap={100}
              >
                {[
                  {
                    label: 'hpa ownership:',
                    content: (
                      <PodCountItem
                        prev={toOwnership(event.details.previous.hpaSettings)}
                        curr={toOwnership(event.details.current.hpaSettings)}
                      />
                    ),
                  },
                  {
                    label: 'basic behavior:',
                    content: (
                      <>
                        <PodCountItem
                          prev={
                            event.details.previous.hpaSettings?.nativeHpaSpec
                              ?.minReplicas
                          }
                          curr={
                            event.details.current.hpaSettings?.nativeHpaSpec
                              ?.minReplicas
                          }
                          label="MIN REPLICAS"
                        />
                        <PodCountItem
                          prev={
                            event.details.previous.hpaSettings?.nativeHpaSpec
                              ?.maxReplicas
                          }
                          curr={
                            event.details.current.hpaSettings?.nativeHpaSpec
                              ?.maxReplicas
                          }
                          label="MAX REPLICAS"
                        />
                      </>
                    ),
                  },
                  {
                    label: 'triggers:',
                    content: (
                      <>
                        {triggerNames.map((name) => {
                          const prev = prevTriggers[name];
                          const curr = currTriggers[name];
                          const triggerType = curr?.type ?? prev?.type;
                          return (
                            <PodCountItem
                              key={name}
                              label={name + ' utilization'}
                              prev={formatTriggerValue(
                                prev?.value,
                                triggerType
                              )}
                              curr={formatTriggerValue(
                                curr?.value,
                                triggerType
                              )}
                            />
                          );
                        })}
                      </>
                    ),
                  },
                ].map(({ content, label }, index) => (
                  <Stack key={index} gap={8}>
                    <DetailWitTitle title={label} titleVariant="strong" />
                    <Stack direction="row" alignItems="center" gap={40}>
                      {content}
                    </Stack>
                  </Stack>
                ))}
              </Box>
            </Stack>
          </ExternalCell>
        </ExternalRow>
      )}
      {hasBehaviorSection && (
        <ExternalRow
          minHeight={240}
          sx={{ alignItems: 'flex-start', py: 12, borderTop: 'none!important' }}
        >
          <ExternalCell startColumn={0}></ExternalCell>
          <ExternalCell startColumn={1} endColumn={2}>
            <DetailWitTitle
              title="AUTOSCALING BEHAVIOR"
              titleVariant="strong"
            />
            <Divider sx={{ width: '100%', my: 6 }} />

            <Stack gap={20} width="100%">
              <Box
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                columnGap={100}
              >
                <PodCountItem
                  label="scale up stabilization:"
                  prev={toSeconds(
                    prevBehavior?.scaleUp?.stabilizationWindowSeconds
                  )}
                  curr={toSeconds(
                    currBehavior?.scaleUp?.stabilizationWindowSeconds
                  )}
                />
                <PodCountItem
                  label="scale down stabilization:"
                  prev={toSeconds(
                    prevBehavior?.scaleDown?.stabilizationWindowSeconds
                  )}
                  curr={toSeconds(
                    currBehavior?.scaleDown?.stabilizationWindowSeconds
                  )}
                />
              </Box>

              <ScalingPoliciesSubsection
                titleSuffix="SCALE UP"
                prevPolicies={prevBehavior?.scaleUp?.policies}
                currPolicies={currBehavior?.scaleUp?.policies}
              />

              <ScalingPoliciesSubsection
                titleSuffix="SCALE DOWN"
                prevPolicies={prevBehavior?.scaleDown?.policies}
                currPolicies={currBehavior?.scaleDown?.policies}
              />
            </Stack>
          </ExternalCell>
        </ExternalRow>
      )}
    </ScalingPolicyEventDetailsRowContainer>
  );
};
